/**
 * Working in a dead spot.
 *
 * Half of these routes have no signal, so the crew has to be able to (a) see
 * where the next stop is and (b) finish a job, with nothing more than the phone
 * in their hand. Both are stored locally and reconciled when signal returns.
 *
 *   'day-cache'      the jobs last fetched, so the schedule still renders cold
 *   'pending-actions' finishes and skips waiting to be sent, with the real time
 *                     they happened
 *
 * The server treats a repeated finish or skip as already done, so a flaky
 * connection cannot complete a job twice, and it accepts `client_ts` so time on
 * site stays honest even when the request lands an hour later.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import { API_BASE_URL } from '../services/api';

const DB_NAME = 'kam-offline';
const VERSION = 2;                       // v1 held only pending-photos
const ACTIONS = 'pending-actions';
const CACHE = 'day-cache';

export type QueuedAction = {
  key?: number;
  jobId: number;
  action: 'complete' | 'skip';
  body: Record<string, any>;
  queuedAt: number;
  label: string;                          // what to show the crew: "Finished — Aubrey Rushing"
};

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains('pending-photos')) db.createObjectStore('pending-photos', { autoIncrement: true });
      if (!db.objectStoreNames.contains(ACTIONS)) db.createObjectStore(ACTIONS, { autoIncrement: true });
      if (!db.objectStoreNames.contains(CACHE)) db.createObjectStore(CACHE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function idb<T>(store: string, mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest): Promise<T> {
  return openDB().then(db => new Promise<T>((resolve, reject) => {
    const req = fn(db.transaction(store, mode).objectStore(store));
    req.onsuccess = () => resolve(req.result as T);
    req.onerror = () => reject(req.error);
  }));
}

/** Keep a copy of whatever the app last saw, so a cold start out of range still shows the day. */
export async function cacheDay(key: string, value: any): Promise<void> {
  try { await idb(CACHE, 'readwrite', s => s.put({ value, at: Date.now() }, key)); } catch { /* storage unavailable */ }
}
export async function readCachedDay<T>(key: string): Promise<{ value: T; at: number } | null> {
  try { return (await idb<any>(CACHE, 'readonly', s => s.get(key))) || null; } catch { return null; }
}

async function allActions(): Promise<QueuedAction[]> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const out: QueuedAction[] = [];
    const req = db.transaction(ACTIONS, 'readonly').objectStore(ACTIONS).openCursor();
    req.onsuccess = e => {
      const c = (e.target as IDBRequest<IDBCursorWithValue | null>).result;
      if (c) { out.push({ key: c.key as number, ...c.value }); c.continue(); } else resolve(out);
    };
    req.onerror = () => reject(req.error);
  });
}

/**
 * Queue a finish or a skip. Returns true when it was stored for later, false
 * when the caller should just send it now.
 */
export async function queueAction(a: Omit<QueuedAction, 'key'>): Promise<boolean> {
  if (navigator.onLine) return false;
  try {
    await idb(ACTIONS, 'readwrite', s => s.add(a));
    return true;
  } catch {
    return false;                          // no IndexedDB — let the caller try the network
  }
}

export function useOfflineWork(onSynced?: () => void) {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pending, setPending] = useState<QueuedAction[]>([]);
  const flushing = useRef(false);

  const refresh = useCallback(async () => {
    try { setPending(await allActions()); } catch { /* ignore */ }
  }, []);

  const flush = useCallback(async () => {
    if (flushing.current || !navigator.onLine) return;
    flushing.current = true;
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const items = await allActions();
      let sent = 0;
      for (const item of items) {
        if ((item as any).kind === 'bidder') continue;   // the bidder's queue (useBidderOffline) sends its own
        try {
          const res = await fetch(`${API_BASE_URL}/jobs/${item.jobId}/${item.action}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ ...item.body, client_ts: item.queuedAt }),
          });
          // 401 / 403 are about the login, not the job: the token expired, or
          // everyone was signed out. The finish still happened — keep it and
          // stop here, so it goes out once they sign back in. Deleting it would
          // throw away a real completion and its on-site time.
          if (res.status === 401 || res.status === 403) break;
          // 400 / 404 / 409 / 422 mean the server refused this one for good
          // (already finished, job deleted, the day has passed). Keep it out of
          // the queue rather than retrying for ever; anything else — 5xx,
          // 408, 429 — is treated as "still no signal".
          if (res.ok || [400, 404, 409, 410, 422].includes(res.status)) {
            await idb(ACTIONS, 'readwrite', s => s.delete(item.key as number));
            if (res.ok) sent++;
          } else break;
        } catch { break; }
      }
      if (sent) {
        toast.success(`${sent} job${sent === 1 ? '' : 's'} sent in now you're back online`);
        onSynced?.();
      }
      await refresh();
    } finally { flushing.current = false; }
  }, [onSynced, refresh]);

  useEffect(() => {
    refresh();
    const up = () => { setIsOnline(true); flush(); };
    const down = () => setIsOnline(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    if (navigator.onLine) flush();
    const t = setInterval(() => { if (navigator.onLine) flush(); }, 60000);
    return () => { window.removeEventListener('online', up); window.removeEventListener('offline', down); clearInterval(t); };
  }, [flush, refresh]);

  return { isOnline, pending, pendingCount: pending.length, flush, refresh };
}

/**
 * Keeps the last good copy of a list and hands it back when the network is
 * gone. `live` is whatever the query returned; the result is what to render.
 *
 *   const { jobs, fromCache, cachedAt } = useDayCache('crew-schedule', data)
 */
export function useDayCache<T>(key: string, live: T | undefined) {
  const [cached, setCached] = useState<{ value: T; at: number } | null>(null);

  useEffect(() => {
    let alive = true;
    readCachedDay<T>(key).then(c => { if (alive && c) setCached(c); });
    return () => { alive = false; };
  }, [key]);

  useEffect(() => {
    if (live === undefined || live === null) return;
    if (Array.isArray(live) && live.length === 0) return;   // don't cache an empty first render over a good copy
    cacheDay(key, live);
    setCached({ value: live, at: Date.now() });
  }, [key, live]);

  const usingCache = (live === undefined || live === null) && !!cached;
  return {
    value: (live ?? cached?.value) as T | undefined,
    fromCache: usingCache,
    cachedAt: cached?.at ?? null,
  };
}
